import { useEffect, useMemo, useState } from "react";
import { EventLayout } from "@/components/EventLayout";
import { Gate } from "@/components/FeatureGate";
import { useEvent } from "@/context/EventContext";
import { formatApiErrorDetail } from "@/lib/api";
import { MoneyInput } from "@/components/MoneyInput";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Wallet, TrendingUp, CircleDollarSign, CalendarClock, Check, Info, BarChart3 } from "lucide-react";

const empty = { category: "Venue", name: "", vendor: "", estimated: 0, notes: "" };
const CATEGORIES = ["Venue", "Catering", "Photography", "Florals", "Music", "Attire", "Stationery", "Décor", "Transport", "Other"];
const statusCls = {
  planned: "bg-stone-100 text-stone-500",
  partial: "bg-[#D4C5B3]/30 text-[#8a744f]",
  paid: "bg-[#8C9A86]/15 text-[#5f6b59]",
};
const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function Budget() {
  const { eapi } = useEvent();
  const [items, setItems] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [summary, setSummary] = useState({ estimated: 0, actual: 0, paid: 0, remaining: 0 });
  const [modal, setModal] = useState(null);

  const load = async () => {
    const [i, t] = await Promise.all([eapi.get("/budget"), eapi.get("/budget/timeline")]);
    setItems(i.data);
    setTimeline(t.data.entries || []);
    setSummary(t.data.summary || { estimated: 0, actual: 0, paid: 0, remaining: 0 });
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (form.id) await eapi.put(`/budget/${form.id}`, form);
      else await eapi.post("/budget", form);
      toast.success("Saved"); setModal(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await eapi.delete(`/budget/${id}`); toast.success("Removed"); load(); };

  const togglePaid = async (entry) => {
    try {
      await eapi.put(`/vendors/${entry.vendor_id}/installments/${entry.installment_id}/paid`, { paid: !entry.paid });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const owedByMonth = useMemo(() => {
    const m = {};
    timeline.forEach((e) => {
      if (e.paid || !e.due_date) return;
      const key = e.due_date.slice(0, 7);
      m[key] = (m[key] || 0) + Number(e.amount || 0);
    });
    return Object.entries(m).sort().map(([k, amount]) => {
      const [y, mo] = k.split("-");
      return { key: k, year: y, mon: MONTHS[parseInt(mo, 10) - 1], amount };
    });
  }, [timeline]);

  const yearGroups = useMemo(() => {
    const g = [];
    owedByMonth.forEach((c) => {
      const last = g[g.length - 1];
      if (last && last.year === c.year) last.span += 1;
      else g.push({ year: c.year, span: 1 });
    });
    return g;
  }, [owedByMonth]);

  return (
    <EventLayout>
      <Gate featureKey="budget">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 lg:py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline">Budget tracker</p>
            <h1 className="font-serif text-4xl tracking-tight">Plan your spending</h1>
          </div>
          <button onClick={() => setModal({ ...empty })} data-testid="add-budget-btn"
            className="flex items-center gap-2 rounded-full bg-[#2C2A28] text-white px-5 py-2.5 text-sm hover:bg-[#8C9A86] transition-colors">
            <Plus size={16} /> Add expense
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Tile icon={Wallet} label="Estimated total" value={money(summary.estimated)} testid="budget-estimated" />
          <Tile icon={TrendingUp} label="Actual (from vendors)" value={money(summary.actual)} testid="budget-actual" />
          <Tile icon={CircleDollarSign} label="Paid so far" value={money(summary.paid)} accent testid="budget-paid" />
          <Tile icon={CircleDollarSign} label="Still owed" value={money(summary.remaining)} testid="budget-remaining" />
        </div>

        <div className="flex items-start gap-2 text-xs text-stone-500 bg-[#8C9A86]/10 border border-[#8C9A86]/20 rounded-lg px-4 py-3 mb-8">
          <Info size={14} className="text-[#5f6b59] shrink-0 mt-0.5" />
          <span>Actual cost and Paid are calculated automatically from your vendors' quotes and payment plans. Mark payments as paid in the timeline below.</span>
        </div>

        <div className="bg-white border border-[#E8E6E1] rounded-lg overflow-hidden mb-8">
          <div className="hidden sm:grid grid-cols-[1.4fr_1fr_0.8fr_0.8fr_0.8fr_auto] gap-4 px-5 py-3 text-[11px] uppercase tracking-wider text-stone-400 border-b border-[#F0EEE9]">
            <span>Item</span><span>Category</span><span>Estimated</span><span>Actual</span><span>Paid</span><span></span>
          </div>
          {items.length === 0 && <p className="p-8 text-center text-sm text-stone-400">No expenses yet.</p>}
          {items.map((it) => (
            <div key={it.id} data-testid={`budget-row-${it.id}`}
              className="grid grid-cols-2 sm:grid-cols-[1.4fr_1fr_0.8fr_0.8fr_0.8fr_auto] gap-2 sm:gap-4 items-center px-5 py-4 border-b border-[#F0EEE9] last:border-0 hover:bg-[#FDFBF7] transition-colors">
              <div className="min-w-0">
                <p className="text-sm font-medium text-stone-800 truncate">{it.name}</p>
                {it.vendor && <p className="text-xs text-stone-400 truncate">{it.vendor}</p>}
              </div>
              <span className="text-sm text-stone-500">{it.category}</span>
              <span className="text-sm text-stone-600">{money(it.estimated)}</span>
              <span className="text-sm text-stone-600">{it.linked_vendor_count ? money(it.actual) : "n/a"}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-stone-600">{it.linked_vendor_count ? money(it.paid) : "n/a"}</span>
                <span className={`hidden lg:inline text-[10px] px-2 py-0.5 rounded-full ${statusCls[it.status] || statusCls.planned}`}>{it.status}</span>
              </div>
              <div className="flex items-center gap-1 justify-end">
                <button onClick={() => setModal({ ...empty, ...it })} data-testid={`edit-budget-${it.id}`} className="p-2 text-stone-400 hover:text-stone-700"><Pencil size={15} /></button>
                <button onClick={() => remove(it.id)} data-testid={`delete-budget-${it.id}`} className="p-2 text-stone-400 hover:text-[#C67A7A]"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-4">
          <CalendarClock size={18} className="text-[#8C9A86]" />
          <h2 className="font-serif text-2xl tracking-tight">Payment timeline</h2>
        </div>
        <div className="bg-white border border-[#E8E6E1] rounded-lg overflow-hidden" data-testid="budget-timeline">
          {timeline.length === 0 ? (
            <p className="p-8 text-center text-sm text-stone-400">Add payment plans to your vendors and their scheduled payments will appear here.</p>
          ) : (
            timeline.map((e) => (
              <div key={e.installment_id} data-testid={`timeline-row-${e.installment_id}`}
                className="flex items-center gap-4 px-5 py-4 border-b border-[#F0EEE9] last:border-0 hover:bg-[#FDFBF7] transition-colors">
                <div className="w-24 shrink-0 text-xs text-stone-500">{e.due_date || "No date"}</div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-stone-800 truncate">{e.vendor_name} · {e.label}</p>
                  {e.budget_item_name && <p className="text-xs text-stone-400 truncate">Budget: {e.budget_item_name}</p>}
                </div>
                <span className="text-sm font-medium text-stone-700 shrink-0">{money(e.amount)}</span>
                <button onClick={() => togglePaid(e)} data-testid={`timeline-toggle-${e.installment_id}`}
                  className={`shrink-0 flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs transition-colors ${e.paid ? "bg-[#8C9A86] text-white hover:bg-[#7a886f]" : "border border-[#E8E6E1] text-stone-600 hover:bg-stone-50"}`}>
                  <Check size={13} /> {e.paid ? "Paid" : "Mark paid"}
                </button>
              </div>
            ))
          )}
        </div>

        {/* Money still owed by month (bottom of page) */}
        <div className="flex items-center gap-2 mb-4 mt-12">
          <BarChart3 size={18} className="text-[#8C9A86]" />
          <h2 className="font-serif text-2xl tracking-tight">Money still owed by month</h2>
        </div>
        <div className="bg-white border border-[#E8E6E1] rounded-lg p-5 overflow-x-auto" data-testid="owed-by-month">
          {owedByMonth.length === 0 ? (
            <p className="text-sm text-stone-400 py-6 text-center">No upcoming unpaid payments. Add vendor payment plans to see your cash flow.</p>
          ) : (
            <table className="w-full text-sm border-collapse min-w-[420px]">
              <thead>
                <tr>
                  {yearGroups.map((g) => (
                    <th key={g.year} colSpan={g.span} className="text-center font-serif text-lg text-stone-700 border-b border-[#E8E6E1] pb-2 px-3">{g.year}</th>
                  ))}
                </tr>
                <tr>
                  {owedByMonth.map((c) => (
                    <th key={c.key} data-testid={`owed-month-${c.key}`} className="text-center text-xs uppercase tracking-wider text-stone-400 font-medium py-3 px-3">{c.mon}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {owedByMonth.map((c) => (
                    <td key={c.key} data-testid={`owed-amount-${c.key}`} className="text-center text-stone-800 font-medium px-3 pt-1">{money(c.amount)}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal && <BudgetModal initial={modal} onClose={() => setModal(null)} onSave={save} />}
      </Gate>
    </EventLayout>
  );
}

function Tile({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className={`rounded-lg border p-5 ${accent ? "bg-[#8C9A86] border-[#8C9A86] text-white" : "bg-white border-[#E8E6E1]"}`}>
      <Icon size={17} className={accent ? "text-white/80" : "text-[#8C9A86]"} />
      <p className="font-serif text-3xl mt-3">{value}</p>
      <p className={`text-xs mt-1 ${accent ? "text-white/80" : "text-stone-500"}`}>{label}</p>
    </div>
  );
}

function BudgetModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-6 fade-up" onClick={(e) => e.stopPropagation()} data-testid="budget-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-2xl">{form.id ? "Edit expense" : "Add expense"}</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <F label="Item name" value={form.name} onChange={(v) => set("name", v)} testid="bm-name" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="text-xs font-medium text-stone-500 mb-1.5 block">Category</span>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="bm-category"
                className="w-full rounded-md border border-[#E8E6E1] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#8C9A86]">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <F label="Vendor (label)" value={form.vendor} onChange={(v) => set("vendor", v)} testid="bm-vendor" />
          </div>
          <label className="block">
            <span className="text-xs font-medium text-stone-500 mb-1.5 block">Estimated</span>
            <MoneyInput value={form.estimated} onChange={(v) => set("estimated", v === "" ? 0 : v)} testid="bm-estimated" />
          </label>
          <p className="text-xs text-stone-400 flex items-start gap-1.5"><Info size={13} className="shrink-0 mt-0.5" /> Actual cost and Paid are tracked through your linked vendors and their payment plans.</p>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 rounded-full border border-[#E8E6E1] py-2.5 text-sm hover:bg-stone-50">Cancel</button>
          <button onClick={() => onSave(form)} data-testid="bm-save" className="flex-1 rounded-full bg-[#2C2A28] text-white py-2.5 text-sm hover:bg-[#8C9A86] transition-colors">Save</button>
        </div>
      </div>
    </div>
  );
}

function F({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-stone-500 mb-1.5 block">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86] focus:ring-2 focus:ring-[#8C9A86]/20" />
    </label>
  );
}
